import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { deleteUser, getUsers } from '../../../../api/endpoints/system';
import { USER_TYPE_LABELS, type SystemUser, type UserTypeCode } from '../../../../api/types/system';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  ListPageScaffold,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { UserFormDialog } from '../UserFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this project's
 *  pinned TypeScript 4.9.5). Ortak `ListPageScaffold` kullanır. */
export default function UserListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<SystemUser | null>(null);
  const [search, setSearch] = useState('');

  const query = useQuery({ queryKey: ['system', 'users'], queryFn: () => getUsers() });
  const rows: SystemUser[] = useMemo(() => query.data?.userList ?? [], [query.data]);

  const typeLabel = (u: SystemUser) =>
    u.userType !== undefined ? USER_TYPE_LABELS[u.userType as UserTypeCode] : '';

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.username, row.firstName, row.lastName, row.email, typeLabel(row), row.gsaId, row.isActive ? 'Aktif' : 'Pasif']
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteUser(id),
    invalidateKeys: [['system', 'users']],
    successMessage: 'Kullanıcı silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.username ?? '';

  const columns: DataTableColumn<SystemUser>[] = [
    { id: 'username', header: 'Kullanıcı Adı', cell: (r) => r.username },
    { id: 'name', header: 'Ad Soyad', cell: (r) => `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || '—' },
    { id: 'email', header: 'E-posta', cell: (r) => r.email ?? '—' },
    { id: 'type', header: 'Tip', cell: (r) => typeLabel(r) || '—' },
    { id: 'gsa', header: 'Firma ID', cell: (r) => r.gsaId ?? '—' },
    { id: 'status', header: 'Durum', cell: (r) => <StatusChip active={r.isActive} /> },
  ];

  return (
    <ListPageScaffold
      title="Kullanıcılar"
      createLabel="Yeni Kullanıcı"
      onCreate={() => setCreateOpen(true)}
      search={{ value: search, onChange: setSearch, placeholder: 'Kullanıcı adı, ad soyad, e-posta...' }}
      query={query}
      rows={filteredRows}
      columns={columns}
      getRowKey={(r) => r.id}
      emptyTitle={rows.length === 0 ? 'Kayıtlı kullanıcı bulunamadı.' : 'Aramayla eşleşen kullanıcı bulunamadı.'}
      rowActions={(r) => <RowActions onEdit={() => setEditing(r)} onDelete={() => del.request(r.id)} />}
    >
      <UserFormDialog open={createOpen} user={null} onClose={() => setCreateOpen(false)} />
      <UserFormDialog open={!!editing} user={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Kullanıcıyı sil"
        description={`"${deletingName}" kullanıcısını silmek istediğinize emin misiniz?`}
      />
    </ListPageScaffold>
  );
}
